import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-allocation-denial-dialog',
  template: `
    <p-dialog 
      [(visible)]="visible" 
      [modal]="true" 
      [closable]="false"
      [draggable]="false"
      [resizable]="false"
      appendTo="body"
      [baseZIndex]="20050"
      styleClass="allocation-denial-dialog"
      [style]="{width: '600px', maxWidth: '90vw'}"
      (onHide)="onClose()">
      
      <div class="allocation-denial-content">
        <!-- Alert Section -->
        <div class="alert">
          <span [innerHTML]="'allocationDenial.alert.message' | translate"></span>
        </div>
        
        <!-- CTA Text -->
        <p class="cta-text" [innerHTML]="'allocationDenial.alert.cta' | translate"></p>

        <!-- Options Section -->
        <label class="option" (click)="selectOption('cancel')">
          <input type="radio" name="option" [checked]="selectedOption === 'cancel'">
          <div class="option-content">
            <div class="option-title">{{ 'allocationDenial.options.cancel.title' | translate }}</div>
            <small class="option-description">{{ 'allocationDenial.options.cancel.description' | translate }}</small>
          </div>
        </label>

        <label class="option" (click)="selectOption('continue-without')">
          <input type="radio" name="option" [checked]="selectedOption === 'continue-without'">
          <div class="option-content">
            <div class="option-title">{{ 'allocationDenial.options.continueWithout.title' | translate }}</div>
            <small class="option-description">{{ 'allocationDenial.options.continueWithout.description' | translate }}</small>
          </div>
        </label>

        <label class="option" (click)="selectOption('reverse-charge')">
          <input type="radio" name="option" [checked]="selectedOption === 'reverse-charge'">
          <div class="option-content">
            <div class="option-title">{{ 'allocationDenial.options.reverseCharge.title' | translate }}</div>
            <small class="option-description">{{ 'allocationDenial.options.reverseCharge.description' | translate }}</small>
          </div>
        </label>

        <label class="option" (click)="selectOption('transfer-unit')">
          <input type="radio" name="option" [checked]="selectedOption === 'transfer-unit'">
          <div class="option-content">
            <div class="option-title">{{ 'allocationDenial.options.transferUnit.title' | translate }}</div>
            <small class="option-description">{{ 'allocationDenial.options.transferUnit.description' | translate }}</small>
          </div>
        </label>

        <!-- Buttons Section -->
        <button class="btn btn-primary" [disabled]="!selectedOption" (click)="onContinue()">
          {{ 'allocationDenial.buttons.continue' | translate }}
        </button>
        <button class="btn btn-secondary" (click)="onNotDecided()">
          {{ 'allocationDenial.buttons.cancel' | translate }}
        </button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .allocation-denial-content {
      font-family: Arial, sans-serif;
      direction: rtl;
      text-align: right;
      padding: 0;
    }

    .alert {
      background-color: #fff3cd;
      color: #856404;
      border: 1px solid #ffeeba;
      padding: 10px;
      border-radius: 5px;
      margin-bottom: 20px;
      font-weight: bold;
      text-align: center;
    }

    .cta-text {
      text-align: center;
      font-weight: bold;
      margin-bottom: 20px;
    }

    .option {
      background-color: white;
      border: 1px solid #ccc;
      padding: 12px;
      border-radius: 8px;
      margin-bottom: 15px;
      cursor: pointer;
      transition: border 0.3s;
      display: flex;
      align-items: flex-start;
    }

    .option:hover {
      border: 1px solid #007bff;
    }

    .option input[type="radio"] {
      margin-left: 15px;
      transform: scale(1.2);
      cursor: pointer;
      flex-shrink: 0;
    }

    .option-content {
      flex: 1;
    }

    .option-title {
      font-weight: normal;
      margin-bottom: 4px;
      line-height: 1.4;
    }

    .option-description {
      color: #666;
      font-size: 0.9em;
      line-height: 1.3;
    }

    .btn {
      display: block;
      width: 100%;
      padding: 12px;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: center;
    }

    .btn-primary {
      background-color: #000;
      color: white;
      margin-top: 20px;
    }

    .btn-primary:hover:not(:disabled) {
      background-color: #333;
    }

    .btn-primary:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }

    .btn-secondary {
      background-color: gray;
      color: white;
      margin-top: 10px;
    }

    .btn-secondary:hover {
      background-color: #555;
    }

    @media (max-width: 600px) {
      .allocation-denial-content {
        padding: 0;
      }
      
      .btn {
        font-size: 18px;
      }
    }

    ::ng-deep .allocation-denial-dialog .p-dialog-header {
      display: none;
    }

    ::ng-deep .allocation-denial-dialog .p-dialog-content {
      padding: 30px;
      background-color: rgba(255, 255, 255, 0.8);
      border-radius: 10px;
      box-shadow: 0 0 20px rgba(0,0,0,0.3);
    }

    ::ng-deep .allocation-denial-dialog .p-dialog-mask {
      background-color: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(2px);
    }

    @media (max-width: 600px) {
      ::ng-deep .allocation-denial-dialog .p-dialog-content {
        margin: 20px;
        padding: 20px;
      }
    }
  `]
})
export class AllocationDenialDialogComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() documentData: any;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() optionSelected = new EventEmitter<{option: string, action: string}>();

  selectedOption: string = '';

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    }

  ngOnChanges() {
    // Reset selection when dialog opens
    if (this.visible) {
      this.selectedOption = '';
    }
  }

  selectOption(option: string) {
    this.selectedOption = option;
  }

  onContinue() {
    if (this.selectedOption) {
      this.optionSelected.emit({
        option: this.selectedOption,
        action: 'continue'
      });
      this.onClose();
    }
  }

  onNotDecided() {
    this.optionSelected.emit({
      option: '',
      action: 'not-decided'
    });
    this.onClose();
  }

  onClose() {
    this.visible = false;
    this.selectedOption = '';
    this.visibleChange.emit(false);
    }
}